import Section from './Section.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { setFormDetailsQuery, useFormDetailsQuery } from '../../lib/Queries';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import EditQuestionPanel from './EditQuestionPanel.jsx';
import { Square3Stack3DIcon } from '@heroicons/react/24/outline/index.js';
import SortableContainers from '../SortableContainers.jsx';
import EditableQuestion from './EditableQuestion.jsx';
import AddQuestionPanel from './AddQuestionPanel.jsx';
import ErrorMessage from '../ErrorMessage';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import FormHeaderBlock from './FormHeaderBlock.jsx';
import EditableFormHeaderBlock from './EditableFormHeaderBlock.jsx';
import FormBackgroundContainer from './FormBackgroundContainer.jsx';
import CustomCssScope from './CustomCssScope.jsx';
import CssInspector from './CssInspector.jsx';
import { useIsCssEditorVisible } from '~/lib/CssInspectorState';
import PageContainer from '../../public/components/PageContainer.jsx';
import Questions from '../../lib/Questions';
import * as Toast from '~/lib/Toast';
import PaymentStepSkeleton from '~/components/forms/PaymentStepSkeleton.jsx';
import Loading from '~/components/Loading';
import WarningMessageBlock from '~/components/WarningMessageBlock.jsx';

const Design = ({
    formId,
    hasClientIdentifier,
    onClickHeader,
    onClickName,
    url,
}) => {
    const isCssEditorVisible = useIsCssEditorVisible();
    const [addToSection, setAddToSection] = useState(null);
    const { data, isLoading } = useFormDetailsQuery(formId);
    const { isSaving: isSavingSection, save: saveSection } = useBusinessPostApi(
        `/forms/${formId}/sections/0`,
    );
    const { isSaving: isReordering, save: saveReorder } = useBusinessPostApi(
        `/forms/${formId}/reorder`,
    );
    const { isSaving: isSavingNewQuestion, save: saveNewQuestion } =
        useBusinessPostApi(
            `/forms/${formId}/sections/${addToSection?.id}/questions/0`,
        );
    const { save: saveAddPaymentSection, isSaving: isSavingAddPaymentSection } = useBusinessPostApi(
        `/forms/${formId}/add-payment-section`,
    );

    const [addQuestionOrder, setAddQuestionOrder] = useState(null);
    const [editQuestion, realSetEditQuestion] = useState(null);
    const [showAddQuestion, setShowAddQuestion] = useState(false);
    const editQuestionPanelRef = useRef();

    const hasPaymentStep = !!data?.hasPaymentStep;
    const sections = data?.sections || [];
    const formSections = data?.formSections || [];
    const formSectionsRef = useRef(formSections);
    formSectionsRef.current = formSections;

    const submitIsHidden = !!data?.form?.settings?.styles?.hideSubmit;

    // Already resolved against the form's theme by the time it gets here — see
    //  FormsController::formDetails
    const formStyles = data?.form?.settings?.styles;

    console.log('data=', data);
    const thankYouSection = sections.filter(
        s => !!s.section.settings?.thankyou,
    )[0];

    const paymentSection = sections.filter(
        s => !!s.section.settings?.payment,
    )[0];

    useEffect(() => {
        if (showAddQuestion && !!editQuestion) {
            setEditQuestion(null);
        }
    }, [showAddQuestion]);

    const setEditQuestion = useCallback(value => {
        realSetEditQuestion(value);
        if (value) setShowAddQuestion(false);
    }, []);

    function setEditQuestionConfirm(value) {
        // If there's currently a different question being edited, confirm they want to discard changes
        if (editQuestion && editQuestionPanelRef?.current?.isDirty()) {
            if (
                !confirm(
                    'You have unsaved changes, do you want to discard them?',
                )
            ) {
                return;
            }
        }
        setEditQuestion(value);
    }

    function addSection() {
        saveSection({}).then(formData => {
            setFormDetailsQuery(formId, formData);
        });
    }

    const sortableItems = {};
    for (const { section, questions } of formSections) {
        sortableItems[' ' + section.id] = questions;
    }

    function onReorder(newItems) {
        // Update local state AND server

        const idOrder = {};
        for (const listId in newItems) {
            idOrder[listId] = newItems[listId].map((q, idx) => ({
                id: q.id,
                order: idx,
            }));
        }

        // Server state
        saveReorder({
            order: idOrder,
        });

        // Local state update
        const newSections = sections.map(sectionData => {
            const section = sectionData.section;
            // console.log(
            //     'section',
            //     section,
            //     'new items',
            //     newItems[' ' + section.id],
            // );
            return {
                ...sectionData,
                questions:
                    typeof newItems[' ' + section.id] !== 'undefined'
                        ? newItems[' ' + section.id]
                        : sectionData.questions,
            };
        });

        const newFormData = {
            ...data,
            sections: newSections,
        };
        setFormDetailsQuery(formId, newFormData);
    }

    function addQuestion(order, section) {
        // Show add question panel
        setAddQuestionOrder(order);
        setAddToSection(section);
        setShowAddQuestion(true);
    }

    function onAddQuestion({ label, type, settings }) {
        saveNewQuestion({
            // A custom question arrives with its own name and the definition
            //  it uses, so that wins over the type's generic defaults
            label:
                label ??
                (Questions[type].dontDefaultLabelToType
                    ? ''
                    : Questions[type].name), // Default
            type: type,
            settings: settings ?? Questions[type].defaultSettings ?? {},
            insertBefore: addQuestionOrder,
        }).then(result => {
            setFormDetailsQuery(formId, result);
            // TODO, open edit question panel
            console.log('Added question', result.question, result);
            setEditQuestionConfirm(result.question);
            setShowAddQuestion(false);
        });
    }

    const onEditableQuestionAddQuestion = useCallback(
        (order, question) => {
            addQuestion(
                order,
                findSectionData(question.form_section_id).section,
            );
        },
        [JSON.stringify(sections.map(s => s.section))],
    );

    function findSectionData(sectionId) {
        for (const sectionData of sections) {
            if (parseInt(sectionData.section.id) === parseInt(sectionId)) {
                return sectionData;
            }
        }
    }

    function moveSection(id, offset) {
        // Local state update
        let newSections = [...formSectionsRef.current];
        id = parseInt(id);
        const idx = newSections.findIndex(s => s.section.id === id);

        const newIdx = idx + offset;
        newSections.splice(newIdx, 0, newSections.splice(idx, 1)[0]);
        newSections = newSections.map((sectionData, idx) => ({
            ...sectionData,
            section: {
                ...sectionData.section,
                order: idx,
            },
        }));
        if (paymentSection) newSections.push(paymentSection);
        newSections.push(thankYouSection);

        const idOrder = {};
        for (const sectionData of newSections) {
            idOrder[' ' + sectionData.section.id] = sectionData.questions.map(
                (q, idx) => ({
                    id: q.id,
                    order: idx,
                }),
            );
        }

        // Server state
        saveReorder({
            order: idOrder,
        });

        const newFormData = {
            ...data,
            sections: newSections,
        };

        setFormDetailsQuery(formId, newFormData);
    }

    const renderContainer = useCallback(
        ({ listId, idx, children }) => {
            const sectionData = findSectionData(listId);
            const matchingSection = sectionData.section;
            const hasQuestions = sectionData.questions.length > 0;

            return (
                <Section
                    idx={idx}
                    section={matchingSection}
                    hasQuestions={hasQuestions}
                    children={children}
                    onAddQuestion={order => addQuestion(order, matchingSection)}
                    onMoveUp={idx > 0 ? () => moveSection(listId, -1) : null}
                    onMoveDown={
                        idx < Object.keys(sortableItems).length - 1
                            ? () => moveSection(listId, 1)
                            : null
                    }
                />
            );
        },
        [JSON.stringify(sections.map(s => s.section))],
    );

    return (
        <div className="max-w-3xl 2xl:max-w-[1050px] pb-8">
            {!isLoading && !hasClientIdentifier && !submitIsHidden && (
                <ErrorMessageBlock className="my-4">
                    There is no question to identify the client (like email),
                    please add a client identifier question to preview/share.
                </ErrorMessageBlock>
            )}
            {submitIsHidden && (
                <WarningMessageBlock className="my-4">
                    Clients cannot submit this form. To change this, click Settings next to the form name and uncheck "Hide Submit button".
                </WarningMessageBlock>
            )}
            <AddQuestionPanel
                isOpen={showAddQuestion}
                onClose={() => setShowAddQuestion(false)}
                onAddQuestion={onAddQuestion}
                section={addToSection}
                isSaving={isSavingNewQuestion}
                hasScheduling={!!data?.hasScheduling}
            />
            <EditQuestionPanel
                ref={editQuestionPanelRef}
                question={editQuestion}
                setQuestion={setEditQuestion}
                formId={formId}
                url={url}
            />

            {isCssEditorVisible && <CssInspector />}

            <CustomCssScope
                css={data?.customCssCompiled}
                objectType="form"
                objectId={data?.form?.id}
            >
                <FormBackgroundContainer
                    styles={formStyles}
                    className="-mx-12 px-12 -my-2 py-2 is-designing-form"
                >
                    <EditableFormHeaderBlock
                        styles={formStyles}
                        onClickCover={onClickHeader}
                        onClickName={onClickName}
                        form={data?.form}
                        sections={formSections}
                        className="mb-0"
                        imageOnly={true}
                    />

                    <PageContainer styles={formStyles}>
                        <EditableFormHeaderBlock
                            styles={formStyles}
                            onClickCover={onClickHeader}
                            onClickName={onClickName}
                            form={data?.form}
                            sections={formSections}
                            className="mb-6"
                            imageOnly={false}
                        />

                        {/******* DEBUG STATIC RENDERING *********/}
                        {/*{Object.keys(sortableItems).map((key, idx) => {*/}
                        {/*    const sectionData = findSectionData(key);*/}
                        {/*    const matchingSection = sectionData.section;*/}
                        {/*    const hasQuestions = sectionData.questions.length > 0;*/}

                        {/*    return (*/}
                        {/*        <Section*/}
                        {/*            key={key}*/}
                        {/*            idx={idx}*/}
                        {/*            section={matchingSection}*/}
                        {/*            hasQuestions={hasQuestions}*/}
                        {/*            onAddQuestion={order =>*/}
                        {/*                addQuestion(order, matchingSection)*/}
                        {/*            }*/}
                        {/*        >*/}
                        {/*            {sortableItems[key].map(question => (*/}
                        {/*                <EditableQuestion*/}
                        {/*                    key={question.id}*/}
                        {/*                    formId={formId}*/}
                        {/*                    formStyles={*/}
                        {/*                        data?.form?.settings?.styles*/}
                        {/*                    }*/}
                        {/*                    isActive={false}*/}
                        {/*                    question={question}*/}
                        {/*                    onEditQuestion={setEditQuestion}*/}
                        {/*                    isEditing={*/}
                        {/*                        question.id === editQuestion?.id*/}
                        {/*                    }*/}
                        {/*                    //onAddQuestion={null}*/}
                        {/*                    onAddQuestion={*/}
                        {/*                        onEditableQuestionAddQuestion*/}
                        {/*                    }*/}
                        {/*                />*/}
                        {/*            ))}*/}
                        {/*        </Section>*/}
                        {/*    );*/}
                        {/*})}*/}
                        {/******* /END DEBUG STATIC RENDERING *********/}
                        <SortableContainers
                            items={sortableItems}
                            setItems={onReorder}
                            renderItem={({ item: question, isActive }) => (
                                <EditableQuestion
                                    key={question.id}
                                    formId={formId}
                                    formStyles={formStyles}
                                    isActive={isActive}
                                    question={question}
                                    onEditQuestion={setEditQuestionConfirm}
                                    isEditing={question.id === editQuestion?.id}
                                    onAddQuestion={onEditableQuestionAddQuestion}
                                />
                            )}
                            Container={renderContainer}
                        />

                        {!submitIsHidden && (
                            <div className="flex justify-center">
                                <button
                                    className="btn-light-pill flex items-center text-sm"
                                    onClick={addSection}
                                >
                                    <Square3Stack3DIcon className="w-4 h-4 mr-2" />
                                    {isSavingSection
                                        ? 'Adding Section...'
                                        : 'Add Section'}
                                </button>
                            </div>
                        )}


                        {!!hasPaymentStep && (
                            <div className="mt-6">
                                <div className="panel-label">
                                    Payment
                                </div>

                                <Section
                                    idx={false}
                                    section={paymentSection?.section || {settings: {payment: true}, is_special: true}}
                                    hasQuestions={true}
                                    isAddingQuestion={isSavingAddPaymentSection}
                                    onAddQuestion={order => {
                                        if (paymentSection) {
                                            console.log('Already have payment section', paymentSection);
                                            addQuestion(order, paymentSection?.section)
                                        } else {
                                            saveAddPaymentSection().then(result => {
                                                setFormDetailsQuery(formId, result);
                                                const newPaymentSection = result.sections.find(s => s.section.settings?.payment);
                                                console.log('Added payment section ', newPaymentSection);
                                                addQuestion(order, newPaymentSection?.section)
                                            });
                                        }
                                    }}
                                    showAddTopOnly={true}
                                >
                                    {!!paymentSection?.questions?.map &&
                                        paymentSection.questions?.map(question => (
                                            <EditableQuestion
                                                key={question.id}
                                                formId={formId}
                                                formStyles={formStyles}
                                                // isActive={isActive}
                                                question={question}
                                                onEditQuestion={
                                                    setEditQuestionConfirm
                                                }
                                                isEditing={
                                                    question.id === editQuestion?.id
                                                }
                                                onAddQuestion={order =>
                                                    addQuestion(
                                                        order,
                                                        paymentSection.section,
                                                    )
                                                }
                                                showAddOnBottom={true}
                                            />
                                        ))}
                                    <PaymentStepSkeleton paymentProcessor={data?.paymentProcessor} />
                                </Section>
                            </div>
                        )}

                        {!!thankYouSection && !submitIsHidden && (
                            <div className="mt-6">
                                <div className="panel-label">
                                    Thank you page after this is submitted
                                </div>
                                <Section
                                    idx={false}
                                    section={thankYouSection.section}
                                    hasQuestions={
                                        thankYouSection.questions?.length > 0
                                    }
                                    onAddQuestion={order =>
                                        addQuestion(order, thankYouSection.section)
                                    }
                                >
                                    {!!thankYouSection.questions?.map &&
                                        thankYouSection.questions?.map(question => (
                                            <EditableQuestion
                                                key={question.id}
                                                formId={formId}
                                                formStyles={formStyles}
                                                // isActive={isActive}
                                                question={question}
                                                onEditQuestion={
                                                    setEditQuestionConfirm
                                                }
                                                isEditing={
                                                    question.id === editQuestion?.id
                                                }
                                                onAddQuestion={order =>
                                                    addQuestion(
                                                        order,
                                                        thankYouSection.section,
                                                    )
                                                }
                                                isThankYou={true}
                                            />
                                        ))}
                                </Section>
                            </div>
                        )}
                    </PageContainer>
                </FormBackgroundContainer>
            </CustomCssScope>
        </div>
    );
};

export default Design;
